import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveServices, createConversationWithService } from "@/hooks/useServices";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { 
  Plane, 
  Building2, 
  Car, 
  Utensils, 
  MoreHorizontal,
  ArrowLeft,
  Loader2
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

// Map service names to icons
const serviceIcons: Record<string, React.ElementType> = {
  "Billets d'avion": Plane,
  "Hotels & Airbnb": Building2,
  "Location de voiture": Car,
  "Restaurants": Utensils,
  "Autres": MoreHorizontal,
};

export default function NewConversationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: services, isLoading, error } = useActiveServices();
  const [creating, setCreating] = useState<string | null>(null);

  const handleSelectService = async (serviceId: string) => {
    if (!user || creating) return;

    setCreating(serviceId);
    try {
      const conversation = await createConversationWithService(user.id, serviceId);
      navigate(`/chat/${conversation.id}`);
    } catch (err) {
      console.error("Failed to create conversation:", err);
      toast.error("Impossible de créer la conversation");
      setCreating(null);
    }
  };

  const getIcon = (serviceName: string) => {
    return serviceIcons[serviceName] || MoreHorizontal;
  };

  return (
    <div className="min-h-screen flex flex-col pb-20 bg-background">
      {/* Header */}
      <header className="px-4 sm:px-6 pt-6 sm:pt-8 pb-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/chat")}
              className="shrink-0 h-9 w-9"
            >
              <ArrowLeft size={18} />
            </Button>
            <h1 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
              Nouvelle demande
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground ml-11 sm:ml-12">
            Sélectionnez un service pour démarrer
          </p>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-28 sm:h-32 rounded-xl" />
              ))}
            </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Erreur de chargement des services</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Réessayer
            </Button>
          </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {services?.map((service) => {
                const Icon = getIcon(service.name);
                const isCreating = creating === service.id;

                return (
                  <button
                    key={service.id}
                    onClick={() => handleSelectService(service.id)}
                    disabled={!!creating}
                    className="relative flex flex-col items-center justify-center gap-2 sm:gap-3 p-4 sm:p-6 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-gold transition-all duration-200 text-center touch-feedback disabled:opacity-50"
                  >
                    {isCreating && (
                      <div className="absolute inset-0 flex items-center justify-center bg-card/80 rounded-xl">
                        <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-primary" />
                      </div>
                    )}
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon size={24} className="sm:hidden text-primary" />
                      <Icon size={28} className="hidden sm:block text-primary" />
                    </div>
                    <span className="font-serif font-medium text-foreground text-xs sm:text-sm">
                      {service.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
